import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { Languages, SunMoon, Check, UserRound } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";
import { Button } from "@core/components/ui/button";
import { Input } from "@core/components/ui/input";
import { Label } from "@core/components/ui/label";
import { useAuth } from "@core/contexts/AuthContext";
import { type AppLocale, SUPPORTED_LOCALES, setLocale } from "@core/i18n";
import { UI_THEMES, type UiThemeId, isUiThemeId } from "@core/lib/uiThemes";
import { cn } from "@core/lib/utils";
import AccountPageTemplate from "./AccountPageTemplate";

// Longueur maximale du nom affiché : au-delà, les listes de votants débordent.
const MAX_FULL_NAME_LENGTH = 100;

const NAMES: Record<AppLocale, string> = {
  fr: "Français",
  en: "English",
  es: "Español",
};

export default function AccountPreferences() {
  const { t, i18n } = useTranslation("common");
  const { user, profile, updateProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const [saved, setSaved] = useState(false);
  const initialName =
    profile?.full_name ??
    (user?.user_metadata?.full_name as string | undefined) ??
    "";
  const [fullName, setFullName] = useState(initialName);
  const [nameStatus, setNameStatus] = useState<"idle" | "saved" | "error">(
    "idle",
  );
  const [nameSaving, setNameSaving] = useState(false);

  // Le profil arrive souvent après le premier rendu : recaler le champ.
  useEffect(() => {
    setFullName(initialName);
  }, [initialName]);
  const [themeSaved, setThemeSaved] = useState(false);

  const current = (i18n.language?.slice(0, 2) as AppLocale) ?? "fr";
  const currentTheme: UiThemeId = theme && isUiThemeId(theme) ? theme : "coral";

  const handleSelect = async (loc: AppLocale) => {
    if (loc === current) return;
    await setLocale(loc);
    // Connecté : persister en base (même valeur que le sélecteur globe du
    // header — source unique `profiles.preferred_language`, aussi utilisée
    // pour la langue des emails).
    if (user) await updateProfile({ preferred_language: loc });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  // Droit de rectification (RGPD art. 16) : le nom est visible des autres
  // participants d'un sondage, l'utilisateur doit pouvoir le corriger.
  const trimmedName = fullName.trim();
  const nameTooLong = trimmedName.length > MAX_FULL_NAME_LENGTH;
  const nameUnchanged = trimmedName === initialName.trim();

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || nameTooLong || nameUnchanged) return;
    setNameSaving(true);
    const result = await updateProfile({ full_name: trimmedName || null });
    setNameSaving(false);
    if (result && "error" in result && result.error) {
      setNameStatus("error");
      return;
    }
    setNameStatus("saved");
    window.setTimeout(() => setNameStatus("idle"), 2500);
  };

  const handleSelectTheme = async (id: UiThemeId) => {
    if (id === currentTheme) return;
    setTheme(id);
    // Connecté : persister en base (même valeur que ThemePicker du header —
    // source unique `profiles.ui_theme`).
    if (user) await updateProfile({ ui_theme: id });
    setThemeSaved(true);
    window.setTimeout(() => setThemeSaved(false), 2500);
  };

  return (
    <AccountPageTemplate
      title={t("account.preferences.title")}
      description={t("account.preferences.description")}
      testId="account-preferences-page"
    >
      {user && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserRound className="h-5 w-5 text-primary" />
              {t("account.preferences.name_label")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSaveName}>
              <p className="text-sm text-muted-foreground">
                {t("account.preferences.name_help")}
              </p>
              <div className="space-y-2">
                <Label htmlFor="account-full-name" className="sr-only">
                  {t("account.preferences.name_label")}
                </Label>
                <Input
                  id="account-full-name"
                  name="full_name"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    setNameStatus("idle");
                  }}
                  placeholder={t("account.preferences.name_placeholder")}
                  aria-invalid={nameTooLong}
                  className="w-full sm:max-w-md"
                  data-testid="account-preferences-name-input"
                />
                {nameTooLong && (
                  <p className="text-sm text-destructive">
                    {t("account.preferences.name_too_long", {
                      max: MAX_FULL_NAME_LENGTH,
                    })}
                  </p>
                )}
              </div>
              <Button
                type="submit"
                disabled={nameSaving || nameTooLong || nameUnchanged}
                data-testid="account-preferences-name-save"
              >
                {t("account.preferences.name_save")}
              </Button>
              {nameStatus === "saved" && (
                <p
                  className="text-sm text-success"
                  data-testid="account-preferences-name-saved"
                >
                  {t("account.preferences.saved")}
                </p>
              )}
              {nameStatus === "error" && (
                <p
                  className="text-sm text-destructive"
                  data-testid="account-preferences-name-error"
                >
                  {t("account.preferences.name_error")}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5 text-primary" />
            {t("account.preferences.language_label")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("account.preferences.language_help")}
          </p>

          <div
            className="flex flex-col gap-2 sm:flex-row sm:flex-wrap"
            role="radiogroup"
            aria-label={t("account.preferences.language_label")}
          >
            {SUPPORTED_LOCALES.map((loc) => {
              const active = loc === current;
              return (
                <Button
                  key={loc}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  variant={active ? "default" : "outline"}
                  className={cn(
                    "justify-start sm:w-40",
                    active && "ring-2 ring-ring",
                  )}
                  onClick={() => void handleSelect(loc)}
                  data-testid={`account-preferences-lang-${loc}`}
                >
                  {active && <Check className="mr-2 h-4 w-4" />}
                  {NAMES[loc]}
                </Button>
              );
            })}
          </div>

          {saved && (
            <p
              className="text-sm text-success"
              data-testid="account-preferences-saved"
            >
              {t("account.preferences.saved")}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SunMoon className="h-5 w-5 text-primary" />
            {t("theme_picker.label")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("theme_picker.help")}
          </p>

          <div
            className="flex flex-col gap-2 sm:flex-row sm:flex-wrap"
            role="radiogroup"
            aria-label={t("theme_picker.label")}
          >
            {UI_THEMES.map((id) => {
              const active = id === currentTheme;
              return (
                <Button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  variant={active ? "default" : "outline"}
                  className={cn(
                    "justify-start sm:w-40",
                    active && "ring-2 ring-ring",
                  )}
                  onClick={() => void handleSelectTheme(id)}
                  data-testid={`account-preferences-theme-${id}`}
                >
                  {active && <Check className="mr-2 h-4 w-4" />}
                  {t(`theme_picker.themes.${id}`)}
                </Button>
              );
            })}
          </div>

          {themeSaved && (
            <p
              className="text-sm text-success"
              data-testid="account-preferences-theme-saved"
            >
              {t("account.preferences.saved")}
            </p>
          )}
        </CardContent>
      </Card>
    </AccountPageTemplate>
  );
}
